import { motion } from "framer-motion";
import { Activity, Pill, Zap, ShieldAlert } from "lucide-react";
import { siteContent } from "@/content/site";

const FEATURE_ICONS = {
  activity: Activity,
  pill: Pill,
  zap: Zap,
  sos: ShieldAlert,
};

const { appShowcase } = siteContent;

export default function AppShowcase() {
  return (
    <section
      id="app"
      data-testid="appshowcase-section"
      className="relative py-20 lg:py-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
            {appShowcase.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36] leading-tight">
            {appShowcase.headingLead}{" "}
            <em className="italic font-medium text-[#1F4E5F]">
              {appShowcase.headingItalic}
            </em>
          </h2>
          <p className="mt-4 text-[#3D5560] text-lg leading-relaxed">
            {appShowcase.body}
          </p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {appShowcase.bullets.map((f, i) => {
              const Icon = FEATURE_ICONS[f.iconKey] || Activity;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-3 rounded-2xl bg-white border border-[#C9DDD6] hover:border-[#1F4E5F]/30 hover:shadow-[0_8px_28px_rgba(15,56,32,0.06)] transition-all"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#E1EEEA] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#1F4E5F]" />
                  </div>
                  <p className="text-sm text-[#3D5560] leading-snug">{f.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="relative flex justify-center"
        >
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#5BAE71]/20 blur-3xl" />
            <div className="absolute bottom-0 right-10 w-80 h-80 rounded-full bg-[#E8A33D]/15 blur-3xl" />
          </div>
          <PhoneMockup imageAlt={appShowcase.imageAlt} />
        </motion.div>
      </div>
    </section>
  );
}

function PhoneMockup({ imageAlt }) {
  return (
    <div className="relative">
      <div className="relative w-[300px] sm:w-[320px] rounded-[44px] bg-[#0E2A36] p-2.5 shadow-[0_40px_80px_-20px_rgba(14,42,54,0.45)]">
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#0E2A36] rounded-b-2xl z-10" />
        <div className="w-full rounded-[36px] overflow-hidden bg-[#F4FAF8]">
          <img
            src="https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/v996dn47_Screenshot_20260608-145002_Careable%20360%2Bplus.jpg"
            alt={imageAlt}
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </div>
      <div className="absolute -bottom-3 -right-3 w-6 h-6 rounded-full bg-[#E8A33D] shadow-md hidden sm:block" />
    </div>
  );
}
